import { useEffect, useState } from "react"
import { useSearchParams, Link } from "react-router-dom"
import { XCircle, HelpCircle, Clock } from "lucide-react"
import { AnimatedCheck } from "@/components/motion/AnimatedCheck"
import { PageHeader, PageSpinner } from "@/components/page"
import api from "../lib/api"
import { trackEvent } from "../lib/meta-pixel"
import { buildEventId } from "@alascorporation/utils"

type PaymentStatus = "loading" | "pending" | "approved" | "declined" | "error"

interface PaymentData {
  id: string
  amount: number
  currency: string
  course_title: string
  courseId: string
}

const SUPPORT_EMAIL = "info@satelab.org"
const POLL_INTERVAL_MS = 4000
const MAX_POLLS = 10

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<PaymentStatus>("loading")
  const [payment, setPayment] = useState<PaymentData | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [courseSlug, setCourseSlug] = useState<string | null>(null)
  const reference = searchParams.get("reference") ?? searchParams.get("ref")
  const transactionId = searchParams.get("id")

  useEffect(() => {
    if (!reference) {
      setStatus("error")
      return
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    api.get(`/academy/payments/verify/${reference}`)
      .then(({ data }) => {
        if (cancelled) return
        const p: PaymentData = data.data
        setPayment(p)
        if (p.id && (data.data.status === "approved")) {
          setStatus("approved")
          trackEvent(
            "Purchase",
            { value: Number(p.amount), currency: p.currency, content_name: p.course_title, content_ids: [p.courseId], content_type: "product" },
            buildEventId("Purchase", p.id),
          )
        } else if (["declined", "voided", "error"].includes(data.data.status)) {
          setStatus("declined")
        } else {
          setStatus("pending")
          if (attempt < MAX_POLLS) {
            timer = setTimeout(() => setAttempt((n) => n + 1), POLL_INTERVAL_MS)
          }
        }
      })
      .catch(() => { if (!cancelled) setStatus("error") })

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [reference, attempt])

  // El verify solo trae courseId: el slug sale de los cursos inscritos (ya con acceso tras aprobar).
  useEffect(() => {
    if (status !== "approved" || !payment?.courseId) return
    let cancelled = false
    api.get("/academy/courses/enrolled/me")
      .then(({ data }) => {
        if (cancelled) return
        const match = (data.data ?? []).find((c: { id: string; slug: string }) => c.id === payment.courseId)
        if (match) setCourseSlug(match.slug)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [status, payment])

  const supportHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Ayuda con mi pago")}&body=${encodeURIComponent(`Referencia: ${reference ?? "—"}`)}`
  const outlineLink = "inline-flex h-11 items-center justify-center rounded-lg border border-border px-6 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px"
  const referenceLine = reference && (
    <p className="text-xs text-muted-foreground">
      Referencia: <span className="tabular-nums text-foreground">{reference}</span>
      {transactionId && <> · Transacción: <span className="tabular-nums text-foreground">{transactionId}</span></>}
    </p>
  )

  return (
    <div className="section-shell flex min-h-[70vh] items-center justify-center py-16">
      <div className="mx-auto max-w-md text-center space-y-6">
        {status === "loading" && (
          <div className="space-y-2">
            <PageHeader title="Verificando tu pago" />
            <PageSpinner label="Verificando tu pago" />
          </div>
        )}

        {status === "pending" && (
          <div className="space-y-6">
            <PageHeader title="Procesando tu pago" />
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Clock className="h-8 w-8" aria-hidden="true" />
            </div>
            <p role="status" aria-live="polite" className="text-muted-foreground">
              Tu banco aún no confirma la transacción. Esto puede tardar un par de minutos; puedes dejar esta página abierta.
            </p>
            {referenceLine}
            {attempt >= MAX_POLLS && (
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm text-muted-foreground">Seguimos sin respuesta. Si el cobro aparece en tu banco, escríbenos y lo verificamos.</p>
                <button type="button" onClick={() => { setStatus("loading"); setAttempt(0) }} className={outlineLink}>
                  Verificar de nuevo
                </button>
                <a href={supportHref} className="text-sm font-medium text-primary underline underline-offset-4">Escribir a soporte</a>
              </div>
            )}
          </div>
        )}

        {status === "approved" && (
          <>
            <div className="motion-pop mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
              <AnimatedCheck className="h-8 w-8" />
            </div>
            <PageHeader title="¡Pago exitoso!" />
            {payment && (
              <p className="text-muted-foreground">
                Ya tienes acceso a <span className="font-semibold text-foreground">{payment.course_title}</span>.
              </p>
            )}
            <Link
              to={courseSlug ? `/courses/${courseSlug}` : "/mis-cursos"}
              className="motion-press inline-flex h-11 items-center justify-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground transition-[filter,transform] hover:brightness-95 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              Ir a mi curso
            </Link>
            {referenceLine}
          </>
        )}

        {status === "declined" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/15 text-destructive">
              <XCircle className="h-8 w-8" strokeWidth={2} aria-hidden="true" />
            </div>
            <PageHeader title="Pago rechazado" />
            <p className="text-muted-foreground">El pago no fue procesado por tu entidad financiera. Puedes intentarlo de nuevo con el mismo método u otro distinto.</p>
            <Link
              to="/courses"
              className={outlineLink}
            >
              Volver a cursos
            </Link>
            {referenceLine}
            <p className="text-sm text-muted-foreground">
              ¿Necesitas ayuda? <a href={supportHref} className="font-medium text-primary underline underline-offset-4">Escríbenos</a>.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <HelpCircle className="h-8 w-8" strokeWidth={2} aria-hidden="true" />
            </div>
            <PageHeader title="No pudimos confirmar tu pago" />
            <p className="text-muted-foreground">
              Si el cobro se realizó en tu banco pero no ves el acceso aquí, escríbenos a soporte con tu número de referencia y lo verificamos manualmente.
            </p>
            <Link
              to="/courses"
              className={outlineLink}
            >
              Volver a cursos
            </Link>
            {referenceLine}
            <p className="text-sm text-muted-foreground">
              ¿Necesitas ayuda? <a href={supportHref} className="font-medium text-primary underline underline-offset-4">Escríbenos</a>.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
